import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';

import { api } from '../lib/api.ts';
import { CARGOS, data as formatarData, mesAnterior } from '../lib/formato.ts';
import { useAuth } from '../contexto/auth.tsx';
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
} from '../componentes/ui.tsx';

interface FuncionarioConferencia {
  funcionarioId: number;
  nome: string;
  cargoCodigo: string | null;
  centroCustoId: number | null;
  nomeCentroCusto: string | null;
  usuarioId: number | null;
  nomeUsuario: string | null;
  incluido: boolean;
  motivoExclusao: string | null;
  excluidoPor: string | null;
  alerta: string | null;
}

interface Provisorio {
  funcionarioId: number;
  nome: string;
  cargoCodigo: string;
  status: string;
  motivo: string | null;
  jaNaReplica: boolean;
}

interface Mapeamento {
  id: string;
  cargoCodigo: string;
  tipo: string;
  valor: number;
  prioridade: number;
  observacao: string | null;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  /** Se vale na competência escolhida. Encerrado continua na lista, marcado. */
  vigente: boolean;
}

interface MapeamentoRetirado {
  cargoCodigo: string;
  funcionarioId: number;
  nome: string;
  centroCustoId: number;
}

interface Conferencia {
  competencia: string;
  data: string;
  mapeamentos: Mapeamento[];
  mapeamentosRetirados: MapeamentoRetirado[];
  divergenciasDeCargo: string[];
  total: number;
  incluidos: number;
  funcionarios: FuncionarioConferencia[];
  provisorios: Provisorio[];
}

export function Funcionarios() {
  const { pode } = useAuth();
  // Elegibilidade e mapeamentos sao do modulo de funcionarios; encerrar um
  // mapeamento e o nivel de exclusao.
  const podeEditar = pode('funcionarios', 'editar');
  const podeExcluir = pode('funcionarios', 'excluir');
  const cache = useQueryClient();

  // A conferência é sempre de uma competência: quem entrou em agosto fica fora
  // de julho, e a decisão de um mês não contamina o outro.
  const [competencia, setCompetencia] = useState(mesAnterior().inicio.slice(0, 7));
  const [excluindo, setExcluindo] = useState<FuncionarioConferencia | null>(null);
  const [motivo, setMotivo] = useState('');

  const [novoMapa, setNovoMapa] = useState(false);
  const [mapaCargo, setMapaCargo] = useState('supervisor');
  const [mapaTipo, setMapaTipo] = useState('funcionario');
  const [mapaValor, setMapaValor] = useState('');
  const [mapaObs, setMapaObs] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['conferencia', competencia],
    queryFn: () => api<Conferencia>(`/funcionarios/conferencia?competencia=${competencia}`),
  });

  const alterar = useMutation({
    mutationFn: (v: { f: FuncionarioConferencia; incluido: boolean; motivo: string }) =>
      api<{ ok: true }>('/funcionarios/elegibilidade', {
        metodo: 'PUT',
        corpo: {
          competencia,
          funcionarioId: v.f.funcionarioId,
          nome: v.f.nome,
          incluido: v.incluido,
          motivo: v.motivo,
        },
      }),
    onSuccess: () => {
      setExcluindo(null);
      setMotivo('');
      void cache.invalidateQueries({ queryKey: ['conferencia'] });
    },
  });

  const criarMapa = useMutation({
    mutationFn: () =>
      api<{ id: string; aviso: string }>('/funcionarios/mapeamentos', {
        metodo: 'POST',
        corpo: {
          cargoCodigo: mapaCargo,
          tipo: mapaTipo,
          valor: Number(mapaValor),
          observacao: mapaObs,
        },
      }),
    onSuccess: () => {
      setNovoMapa(false);
      setMapaValor('');
      setMapaObs('');
      void cache.invalidateQueries({ queryKey: ['conferencia'] });
    },
  });

  const encerrarMapa = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/funcionarios/mapeamentos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['conferencia'] }),
  });

  if (isLoading) return <Carregando texto="Consultando a réplica do Feegow…" />;
  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar os funcionários">
        <p>{error.message}</p>
      </Aviso>
    );
  }
  if (data === undefined) return null;

  const semVinculo = data.funcionarios.filter((f) => f.usuarioId === null && f.incluido);
  const excluidos = data.funcionarios.filter((f) => !f.incluido);

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Quem entra no fechamento"
        descricao="Todo funcionário que a réplica traz entra por padrão. Aqui você tira do mês quem não deve participar — a decisão vale só para esta competência."
        acoes={
          <div className="w-52">
            <Campo
              rotulo="Competência"
              tipo="month"
              valor={competencia}
              onChange={setCompetencia}
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <span>
              <strong className="font-mono text-lg">{data.incluidos}</strong> participam
            </span>
            {excluidos.length > 0 && (
              <span className="text-slate-600">
                <strong className="font-mono text-lg">{excluidos.length}</strong> fora do mês
              </span>
            )}
            <span className="text-slate-500">
              {data.total} encontrados na réplica em {data.data}
            </span>
          </div>

          {semVinculo.length > 0 && (
            <Aviso titulo={`${semVinculo.length} funcionário(s) sem usuário do sistema`}>
              {semVinculo.map((f) => (
                <p key={f.funcionarioId}>
                  · {f.nome} — nenhum agendamento será atribuído a esta pessoa. Se ela não deveria
                  participar deste mês, tire do fechamento.
                </p>
              ))}
            </Aviso>
          )}

          {data.mapeamentosRetirados.length > 0 && (
            <Aviso tom="info" titulo="Cadastro manual retirado automaticamente">
              {data.mapeamentosRetirados.map((m) => (
                <p key={m.funcionarioId}>
                  · {m.nome} — o centro de custo {m.centroCustoId} passou a resolver o cargo{' '}
                  {CARGOS[m.cargoCodigo] ?? m.cargoCodigo} na réplica, então a inserção manual não é
                  mais necessária e saiu de cena.
                </p>
              ))}
            </Aviso>
          )}

          {data.divergenciasDeCargo.length > 0 && (
            <Aviso titulo="Divergência entre o cadastro manual e a réplica">
              {data.divergenciasDeCargo.map((d) => (
                <p key={d}>· {d}</p>
              ))}
            </Aviso>
          )}

          {alterar.isError && (
            <Aviso tom="erro">
              <p>{alterar.error.message}</p>
            </Aviso>
          )}
        </div>
      </Cartao>

      {excluindo !== null && (
        <Cartao titulo={`Tirar ${excluindo.nome} do fechamento de ${competencia}`}>
          <div className="max-w-xl space-y-4">
            <Aviso tom="atencao">
              <p>
                Ela não vai aparecer no cálculo desta competência, e também não entra na média da
                equipe — o que muda o valor de todos os outros.
              </p>
              <p>Outros meses não são afetados: a decisão é só de {competencia}.</p>
            </Aviso>

            <Campo
              rotulo="Motivo"
              valor={motivo}
              onChange={setMotivo}
              dica="Ex.: admitida em 04/08, não trabalhou no período. Fica registrado no fechamento e na auditoria."
            />

            <div className="flex gap-3">
              <Botao
                tipo="perigo"
                onClick={() => alterar.mutate({ f: excluindo, incluido: false, motivo })}
                carregando={alterar.isPending}
                desabilitado={motivo.trim().length < 3}
              >
                Tirar do fechamento
              </Botao>
              <Botao
                tipo="secundario"
                onClick={() => {
                  setExcluindo(null);
                  setMotivo('');
                }}
              >
                Cancelar
              </Botao>
            </div>
          </div>
        </Cartao>
      )}

      <Cartao titulo={`Funcionários · competência ${data.competencia}`}>
        <Tabela
          cabecalho={
            <tr>
              <th className="py-2 pr-3">Participa</th>
              <th className="py-2 pr-3">Cargo</th>
              <th className="py-2 pr-3">Funcionário</th>
              <th className="py-2 pr-3 text-right">ID</th>
              <th className="py-2 pr-3">Centro de custo</th>
              <th className="py-2 pr-3">Usuário do sistema</th>
              <th className="py-2 pr-3">Situação</th>
            </tr>
          }
        >
          {data.funcionarios.map((f) => (
            <tr key={f.funcionarioId} className={f.incluido ? 'hover:bg-slate-50' : 'bg-slate-50'}>
              <td className="py-2 pr-3">
                {podeEditar ? (
                  f.incluido ? (
                    <button
                      onClick={() => {
                        setExcluindo(f);
                        setMotivo('');
                      }}
                      title="Tirar deste fechamento"
                      className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2 py-1 text-xs
                                 text-slate-700 hover:border-red-300 hover:bg-red-50 hover:text-red-800"
                    >
                      <UserMinus className="size-3.5" />
                      Tirar
                    </button>
                  ) : (
                    <button
                      onClick={() => alterar.mutate({ f, incluido: true, motivo: '' })}
                      disabled={alterar.isPending}
                      title="Devolver ao fechamento"
                      className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2 py-1 text-xs
                                 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
                    >
                      <UserPlus className="size-3.5" />
                      Incluir
                    </button>
                  )
                ) : (
                  <Etiqueta tom={f.incluido ? 'bom' : 'neutro'}>
                    {f.incluido ? 'sim' : 'não'}
                  </Etiqueta>
                )}
              </td>
              <td className="py-2 pr-3">
                <Etiqueta tom={f.cargoCodigo === 'atendente' ? 'neutro' : 'bom'}>
                  {CARGOS[f.cargoCodigo ?? ''] ?? f.cargoCodigo}
                </Etiqueta>
              </td>
              <td className={f.incluido ? 'py-2 pr-3' : 'py-2 pr-3 text-slate-400 line-through'}>
                {f.nome}
              </td>
              <td className="num py-2 pr-3 text-slate-500">{f.funcionarioId}</td>
              <td className="py-2 pr-3 text-slate-600">
                {f.nomeCentroCusto ?? '—'}
                {f.centroCustoId !== null && (
                  <span className="ml-1 text-xs text-slate-400">({f.centroCustoId})</span>
                )}
              </td>
              <td className="py-2 pr-3 text-slate-600">{f.nomeUsuario ?? '—'}</td>
              <td className="py-2 pr-3">
                {!f.incluido ? (
                  <span className="text-xs text-slate-600">
                    Fora do mês
                    {f.motivoExclusao !== null && <>: {f.motivoExclusao}</>}
                    {f.excluidoPor !== null && (
                      <span className="text-slate-400"> · {f.excluidoPor}</span>
                    )}
                  </span>
                ) : f.alerta === null ? (
                  <Etiqueta tom="bom">ok</Etiqueta>
                ) : (
                  <Etiqueta tom="ruim">sem vínculo</Etiqueta>
                )}
              </td>
            </tr>
          ))}
        </Tabela>
      </Cartao>

      {data.provisorios.length > 0 && (
        <Cartao
          titulo="Cadastros provisórios"
          descricao="Informados à mão porque a réplica é D-1. A conciliação substitui pelo dado oficial quando o ID e o centro de custo baterem."
        >
          <Tabela
            cabecalho={
              <tr>
                <th className="py-2 pr-3">Funcionário</th>
                <th className="py-2 pr-3 text-right">ID</th>
                <th className="py-2 pr-3">Cargo</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Motivo</th>
              </tr>
            }
          >
            {data.provisorios.map((p) => (
              <tr key={p.funcionarioId}>
                <td className="py-2 pr-3">{p.nome}</td>
                <td className="num py-2 pr-3 text-slate-500">{p.funcionarioId}</td>
                <td className="py-2 pr-3">{CARGOS[p.cargoCodigo] ?? p.cargoCodigo}</td>
                <td className="py-2 pr-3">
                  <Etiqueta
                    tom={
                      p.status === 'confirmado' ? 'bom' : p.status === 'divergente' ? 'ruim' : 'medio'
                    }
                  >
                    {p.status.replace(/_/g, ' ')}
                  </Etiqueta>
                </td>
                <td className="py-2 pr-3 text-slate-600">{p.motivo ?? '—'}</td>
              </tr>
            ))}
          </Tabela>
        </Cartao>
      )}

      <Cartao
        titulo="Como o cargo é resolvido"
        descricao="Maior prioridade vence. O cadastro por funcionário serve para não esperar o D-1 da réplica, e o sistema o retira sozinho quando o centro de custo da pessoa passa a resolver o mesmo cargo."
        acoes={
          podeEditar && (
            <Botao tipo="secundario" onClick={() => setNovoMapa(!novoMapa)}>
              <Plus className="size-4" />
              Novo mapeamento
            </Botao>
          )
        }
      >
        {novoMapa && (
          <div className="mb-6 rounded border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Selecao
                rotulo="Cargo"
                valor={mapaCargo}
                onChange={setMapaCargo}
                opcoes={['atendente', 'supervisor', 'backoffice']}
                vazio=""
              />
              <Selecao
                rotulo="Critério"
                valor={mapaTipo}
                onChange={setMapaTipo}
                opcoes={['funcionario', 'centro_custo']}
                vazio=""
              />
              <Campo
                rotulo={mapaTipo === 'funcionario' ? 'ID no Feegow' : 'Centro de custo'}
                valor={mapaValor}
                onChange={setMapaValor}
                dica={
                  mapaTipo === 'funcionario'
                    ? 'O id da pessoa na tabela de funcionários.'
                    : 'O número do centro de custo.'
                }
              />
              <Campo rotulo="Observação" valor={mapaObs} onChange={setMapaObs} />
            </div>

            <div className="mt-4">
              <Aviso tom="info">
                {mapaTipo === 'funcionario' ? (
                  <p>
                    Vale só para esta pessoa e tem prioridade sobre o centro de custo. Quando a
                    réplica trouxer o centro de custo dela apontando para o mesmo cargo, o sistema
                    retira este cadastro sozinho.
                  </p>
                ) : (
                  <p>
                    Mapear um centro de custo afeta <strong>todos</strong> os funcionários dele —
                    por isso só quem administra regras pode fazer.
                  </p>
                )}
              </Aviso>
            </div>

            {criarMapa.isError && (
              <div className="mt-4">
                <Aviso tom="erro">
                  <p>{criarMapa.error.message}</p>
                </Aviso>
              </div>
            )}

            <div className="mt-4 flex gap-3">
              <Botao
                onClick={() => criarMapa.mutate()}
                carregando={criarMapa.isPending}
                desabilitado={mapaValor.trim() === ''}
              >
                Cadastrar
              </Botao>
              <Botao tipo="secundario" onClick={() => setNovoMapa(false)}>
                Cancelar
              </Botao>
            </div>
          </div>
        )}

        {encerrarMapa.isError && (
          <div className="mb-4">
            <Aviso tom="erro">
              <p>{encerrarMapa.error.message}</p>
            </Aviso>
          </div>
        )}

        <Tabela
          cabecalho={
            <tr>
              <th className="py-2 pr-3">Cargo</th>
              <th className="py-2 pr-3">Critério</th>
              <th className="py-2 pr-3 text-right">Valor</th>
              <th className="py-2 pr-3 text-right">Prioridade</th>
              <th className="py-2 pr-3">Situação</th>
              <th className="py-2 pr-3">Observação</th>
              {podeExcluir && <th className="py-2" />}
            </tr>
          }
        >
          {data.mapeamentos.map((m) => (
            <tr key={m.id} className={m.vigente ? '' : 'bg-slate-50 text-slate-400'}>
              <td className="py-2 pr-3">{CARGOS[m.cargoCodigo] ?? m.cargoCodigo}</td>
              <td className="py-2 pr-3">
                <span className={m.vigente ? 'text-slate-600' : ''}>
                  {m.tipo === 'funcionario' ? 'funcionário' : 'centro de custo'}
                </span>
                {m.tipo === 'funcionario' && m.vigente && (
                  <Etiqueta tom="medio">
                    <span className="ml-1">manual</span>
                  </Etiqueta>
                )}
              </td>
              <td className="num py-2 pr-3">{m.valor}</td>
              <td className="num py-2 pr-3">{m.prioridade}</td>
              <td className="py-2 pr-3">
                {m.vigente ? (
                  <Etiqueta tom="bom">em vigor</Etiqueta>
                ) : (
                  <Etiqueta tom="neutro">encerrado em {formatarData(m.vigenciaFim)}</Etiqueta>
                )}
              </td>
              <td className="max-w-96 py-2 pr-3 text-xs">{m.observacao ?? '—'}</td>
              {podeExcluir && (
                <td className="py-2">
                  {m.vigente && (
                    <button
                      onClick={() => encerrarMapa.mutate(m.id)}
                      disabled={encerrarMapa.isPending}
                      title="Encerrar a vigência deste mapeamento"
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-700"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </Tabela>

        <p className="mt-3 text-xs text-slate-500">
          Encerrar não apaga o histórico: fechamentos de meses anteriores continuam resolvendo o
          cargo do jeito que resolviam na época.
        </p>
      </Cartao>
    </div>
  );
}
